import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { Badge } from '@/ui/Badge';
import { Card, CardHeader, CardBody } from '@/ui/Card';
import { Drawer } from '@/ui/Drawer';
import { Dialog, ConfirmDialog } from '@/ui/Dialog';
import { Skeleton, SkeletonFilas, SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoVacio, EstadoFiltroVacio } from '@/components/EstadoVacio';
import { EstadoError } from '@/components/EstadoError';

/**
 * Sistema — el catálogo vivo del design system.
 *
 * No es una demo: es la superficie donde se comprueba el sistema. Un componente
 * se considera terminado cuando aparece aquí **con todos sus estados a la vez**,
 * porque los estados que no se ven juntos no se comparan, y los que no se
 * comparan acaban divergiendo. Es lo que le pasó al portal vanilla: cinco
 * variantes de botón donde el producto necesitaba cuatro, una definida y nunca
 * usada, y otra sin definir.
 *
 * También es la primera pantalla que se monta con la capa React, así que sirve
 * de prueba de que el puente funciona: si esto se ve, el shell vanilla sabe
 * montar y desmontar React dentro de su propio contenedor.
 *
 * Se llega por `#sistema` y solo lo ve el personal de gestión.
 */

function Seccion({ titulo, nota, children }: {
  titulo: string; nota?: string; children: React.ReactNode;
}) {
  return (
    <section className="border-t border-line pt-6">
      <h2 className="text-6 font-bold tracking-tight text-brand-ink">{titulo}</h2>
      {nota && <p className="mt-1 max-w-prose text-3 leading-relaxed text-muted">{nota}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Fila({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-line py-3 last:border-0 md:flex-row md:items-center md:gap-6">
      <div className="w-40 shrink-0 text-2 font-semibold uppercase tracking-wide text-muted">
        {etiqueta}
      </div>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

export function Sistema() {
  const [panel, setPanel] = useState(false);
  const [panelAbajo, setPanelAbajo] = useState(false);
  const [dialogo, setDialogo] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [texto, setTexto] = useState('');

  return (
    <div className="react-root flex flex-col gap-8 pb-12">
      <header>
        <p className="text-2 font-semibold uppercase tracking-wider text-muted">
          Capa React · convive con la SPA vanilla
        </p>
        <h1 className="mt-1 text-8 font-bold tracking-tight text-brand-ink">Sistema de diseño</h1>
        <p className="mt-2 max-w-prose text-4 leading-relaxed text-ink-2">
          Cada componente con todos sus estados a la vez. Cambia el tema desde la paleta
          de la cabecera: nada de lo que hay aquí lleva un color escrito a mano, así que
          todo debería seguir leyéndose igual de bien en claro y en oscuro.
        </p>
      </header>

      {/* ── Botones ─────────────────────────────────────────────── */}
      <Seccion
        titulo="Botones"
        nota="Cuatro niveles, y lo que los separa es el relleno, no el tono. El naranja no
              pinta botones dentro de la aplicación: se reserva para dónde estás (el menú)
              y para la puerta (la pantalla de acceso)."
      >
        <Fila etiqueta="Jerarquía">
          <Button variant="primary">Guardar cambios</Button>
          <Button variant="secondary">Vista previa</Button>
          <Button variant="ghost">Cancelar</Button>
          <Button variant="destructive">Eliminar</Button>
        </Fila>
        <Fila etiqueta="Tamaño">
          <Button size="sm">Pequeño</Button>
          <Button size="md">Normal</Button>
          <Button size="sm" iconOnly aria-label="Ver detalle">
            <i className="bi bi-eye" aria-hidden="true" />
          </Button>
        </Fila>
        <Fila etiqueta="Cargando">
          <Button loading>Guardar cambios</Button>
          <Button variant="secondary" loading>Vista previa</Button>
          <Button
            onClick={() => { setCargando(true); setTimeout(() => setCargando(false), 1600); }}
            loading={cargando}
          >
            Pruébalo
          </Button>
        </Fila>
        <Fila etiqueta="Apagado">
          <Button disabled razonDeshabilitado="Faltan soportes obligatorios por adjuntar">
            Aprobar
          </Button>
          <Button variant="secondary" disabled razonDeshabilitado="Ya publicaste esta plantilla">
            Publicar
          </Button>
          <span className="text-2 text-muted">
            Pasa por encima: un botón apagado tiene que decir por qué.
          </span>
        </Fila>
      </Seccion>

      {/* ── Campos ──────────────────────────────────────────────── */}
      <Seccion
        titulo="Campos"
        nota="Etiqueta siempre encima. Se marca lo opcional, no lo obligatorio: en estos
              formularios casi todo es obligatorio, así que el asterisco sería ruido en
              catorce de quince campos. El error sustituye a la ayuda, no se apila debajo."
      >
        <div className="grid max-w-3xl gap-4 md:grid-cols-2">
          <Field label="Nombre completo" ayuda="Como aparece en tu documento de identidad.">
            <Input placeholder="Ana María Restrepo" value={texto} onChange={(e) => setTexto(e.target.value)} />
          </Field>
          <Field label="Segundo apellido" opcional>
            <Input placeholder="—" />
          </Field>
          <Field label="Correo corporativo" error="Ese correo ya está en uso por otro colaborador.">
            <Input type="email" defaultValue="ana.restrepo@empresa.com" />
          </Field>
          <Field label="Área" ayuda="Determina quién aprueba tus solicitudes.">
            <Select defaultValue="bienestar">
              <option value="bienestar">Bienestar</option>
              <option value="tecnologia">Tecnología</option>
              <option value="operaciones">Operaciones</option>
            </Select>
          </Field>
          <Field label="Sede" ayuda="No se puede cambiar desde aquí.">
            <Input defaultValue="Medellín" disabled />
          </Field>
          <Field label="Motivo" ayuda="Lo lee quien aprueba." className="md:col-span-2">
            <Textarea placeholder="Cuéntanos brevemente para qué necesitas el permiso…" />
          </Field>
        </div>
      </Seccion>

      {/* ── Insignias ───────────────────────────────────────────── */}
      <Seccion
        titulo="Insignias de estado"
        nota="Siempre con su palabra, nunca solo color: alrededor del 8 % de los hombres no
              distingue el verde del rojo. Radio de 4 px y no píldora — la píldora se
              reserva a los filtros, que sí se pulsan."
      >
        <Fila etiqueta="Tonos">
          <Badge tone="neutral">Borrador</Badge>
          <Badge tone="info">En proceso</Badge>
          <Badge tone="ok">Aprobada</Badge>
          <Badge tone="warn">Devuelta</Badge>
          <Badge tone="danger">Rechazada</Badge>
        </Fila>
        <Fila etiqueta="Con icono">
          <Badge tone="warn">
            <i className="bi bi-clock-history" aria-hidden="true" /> 4 días esperando
          </Badge>
          <Badge tone="ok">
            <i className="bi bi-check2" aria-hidden="true" /> Certificado listo
          </Badge>
        </Fila>
      </Seccion>

      {/* ── Superficies ─────────────────────────────────────────── */}
      <Seccion
        titulo="Superficies"
        nota="Plana con filete de 1 px en reposo. La sombra se reserva a lo que de verdad
              está por encima del plano: panel, menú, diálogo, aviso. Y no se anidan
              tarjetas: si algo dentro necesita agruparse, lo separa un filete."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader titulo="En reposo" meta="Sin sombra" />
            <CardBody>
              <p className="text-3 text-ink-2">
                Con doce tarjetas en pantalla, doce sombras apiladas no dan jerarquía: dan
                suciedad. Si todo flota, nada flota.
              </p>
            </CardBody>
          </Card>
          <Card interactiva>
            <CardHeader titulo="Pulsable" meta="Sombra al pasar por encima" />
            <CardBody>
              <p className="text-3 text-ink-2">
                Aquí la sombra sí vale: solo hay una tarjeta bajo el puntero a la vez, así
                que no se apilan, y es la señal de que se puede pulsar.
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardHeader
              titulo="Con acción"
              meta="Actualizado hace 2 h"
              accion={<Button size="sm" variant="ghost" iconOnly aria-label="Más acciones">
                <i className="bi bi-three-dots" aria-hidden="true" />
              </Button>}
            />
            <CardBody>
              <p className="text-3 text-ink-2">
                El título va a 13,5 px en navy y el metadato a 12 px en gris: dos señales
                —tamaño y color— en lugar de colgar la jerarquía solo del grosor.
              </p>
            </CardBody>
          </Card>
        </div>
      </Seccion>

      {/* ── Paneles y diálogos ──────────────────────────────────── */}
      <Seccion
        titulo="Paneles y diálogos"
        nota="El panel lateral es la pieza que más faltaba: el producto tenía 95 modales,
              45 confirmaciones y cero paneles. El diálogo se reserva a confirmar algo
              irreversible y a las barreras legales; todo lo demás es panel."
      >
        <Fila etiqueta="Panel">
          <Button variant="secondary" onClick={() => setPanel(true)}>Abrir panel lateral</Button>
          <Button variant="secondary" onClick={() => setPanelAbajo(true)}>Panel desde abajo</Button>
        </Fila>
        <Fila etiqueta="Diálogo">
          <Button variant="secondary" onClick={() => setDialogo(true)}>Diálogo</Button>
          <Button variant="destructive" onClick={() => setConfirmar(true)}>Confirmar borrado</Button>
        </Fila>

        <Drawer
          abierto={panel}
          onCambio={setPanel}
          titulo="Solicitud SOL-2026-0418"
          descripcion="Permiso remunerado · Ana María Restrepo"
          pie={
            <>
              <Button variant="ghost" onClick={() => setPanel(false)}>Devolver</Button>
              <Button onClick={() => setPanel(false)}>Aprobar</Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <Badge tone="warn"><i className="bi bi-clock-history" aria-hidden="true" /> 4 días esperando</Badge>
            <p className="text-3 leading-relaxed text-ink-2">
              Con el detalle en un panel, la cola sigue ahí detrás. Revisar diez solicitudes
              deja de ser abrir y cerrar diez modales perdiendo la lista cada vez.
            </p>
            <Field label="Nota para el solicitante" opcional>
              <Textarea rows={3} placeholder="Solo si devuelves la solicitud…" />
            </Field>
          </div>
        </Drawer>

        <Drawer
          abierto={panelAbajo}
          onCambio={setPanelAbajo}
          lado="abajo"
          titulo="Filtrar solicitudes"
          descripcion="En móvil el panel entra desde abajo: alcanzar el pulgar es más fácil que alcanzar el borde."
          pie={<Button onClick={() => setPanelAbajo(false)}>Aplicar</Button>}
        >
          <Field label="Estado">
            <Select defaultValue="abiertas">
              <option value="abiertas">En curso</option>
              <option value="cerradas">Cerradas</option>
              <option value="todas">Todas</option>
            </Select>
          </Field>
        </Drawer>

        <Dialog
          abierto={dialogo}
          onCambio={setDialogo}
          titulo="Antes de continuar"
          descripcion="Un diálogo interrumpe. Si lo de detrás sigue importando, no era un diálogo: era un panel."
          pie={<Button onClick={() => setDialogo(false)}>Entendido</Button>}
        />

        <ConfirmDialog
          abierto={confirmar}
          onCambio={setConfirmar}
          destructivo
          titulo="Eliminar la plantilla «Permiso remunerado»"
          descripcion="Se eliminará la plantilla y sus 14 campos. Las 312 solicitudes ya radicadas con ella no se tocan. Esta acción no se puede deshacer."
          textoConfirmar="Eliminar plantilla"
          onConfirmar={() => setConfirmar(false)}
        />
      </Seccion>

      {/* ── Cargando ────────────────────────────────────────────── */}
      <Seccion
        titulo="Cargando"
        nota="Con la forma de lo que viene, no un disco girando. El ojo coloca el contenido
              antes de que llegue, y la espera se siente más corta aunque dure lo mismo."
      >
        <div className="flex flex-col gap-6">
          <div>
            <p className="mb-2 text-2 font-semibold uppercase tracking-wide text-muted">Tabla</p>
            <Card><SkeletonFilas filas={4} columnas={5} /></Card>
          </div>
          <div>
            <p className="mb-2 text-2 font-semibold uppercase tracking-wide text-muted">Rejilla</p>
            <SkeletonTarjetas n={3} />
          </div>
          <div>
            <p className="mb-2 text-2 font-semibold uppercase tracking-wide text-muted">Suelto</p>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-5 w-64" />
              <Skeleton className="h-3 w-96" />
              <Skeleton className="h-3 w-80" />
            </div>
          </div>
        </div>
      </Seccion>

      {/* ── Vacíos y errores ────────────────────────────────────── */}
      <Seccion
        titulo="Vacíos y errores"
        nota="Un vacío dice por qué está vacío. Y «no hay nada creado» no es lo mismo que
              «tu filtro no encontró nada»: confundirlos manda a alguien a crear algo que
              ya tenía delante."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader titulo="Nada creado todavía" />
            <EstadoVacio
              icono="bi-clipboard-check"
              titulo="Aún no has enviado solicitudes"
              descripcion="Cuando envíes una, aquí verás en qué va y quién la tiene ahora mismo."
              accion={{ texto: 'Ver el catálogo', onClick: () => {} }}
            />
          </Card>
          <Card>
            <CardHeader titulo="El filtro no encontró nada" />
            <EstadoFiltroVacio total={312} que="solicitudes" onLimpiar={() => {}} />
          </Card>
          <Card>
            <CardHeader titulo="Sin permiso (403)" meta="Reintentar no arregla nada" />
            <EstadoError
              err={Object.assign(new Error('Forbidden'), { status: 403 })}
              que="esta sección"
              onVolver={() => {}}
            />
          </Card>
          <Card>
            <CardHeader titulo="Fallo del servidor (503)" meta="Aquí sí tiene sentido reintentar" />
            <EstadoError
              err={Object.assign(new Error('Service Unavailable'), { status: 503 })}
              que="tus solicitudes"
              onReintentar={() => {}}
            />
          </Card>
        </div>
      </Seccion>
    </div>
  );
}
